import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { fetchAgentStrategy, saveAgentStrategy, type AgentStrategyConfig } from '../../utils/telegramAuth';

const PageContainer = styled.div`
  width: 100%;
  min-height: calc(100vh - 124px);
  padding: 16px 16px 80px 16px;
  box-sizing: border-box;
  color: #ffffff;
`;

const Card = styled.div`
  background: #0a0a0a;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 16px;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  font-size: 20px;
  margin: 0 0 12px 0;
`;

const Label = styled.label`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  display: block;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 10px;
  padding: 10px 12px;
  color: #ffffff;
  font-size: 12px;
  box-sizing: border-box;
`;

const Select = styled.select`
  width: 100%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 10px;
  padding: 10px 12px;
  color: #ffffff;
  font-size: 12px;
  box-sizing: border-box;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
`;

const HelperText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 6px;
`;

const ActionButton = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.05);
  color: #ffffff;
  padding: 12px 10px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease;
  margin-top: 12px;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const defaultStrategy: AgentStrategyConfig = {
  mode: 'paper',
  cadenceMinutes: 10,
  minSolBalance: 0.2,
  maxTradeSol: 0.05,
  maxDailyTrades: 10,
  allowedMints: []
};

const SentryAgentStrategyPWA: React.FC = () => {
  const [strategy, setStrategy] = useState<AgentStrategyConfig>(defaultStrategy);
  const [enabled, setEnabled] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const result = await fetchAgentStrategy();
      if (cancelled) return;
      if (result?.strategy) {
        setStrategy({ ...defaultStrategy, ...result.strategy });
        setEnabled(Boolean(result.enabled));
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const ok = await saveAgentStrategy({ strategy, enabled });
      setStatus(ok ? 'Strategy saved.' : 'Save failed.');
    } catch {
      setStatus('Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageContainer>
      <Title>Strategy</Title>
      <Card>
        <Label>Mode</Label>
        <Select
          value={strategy.mode}
          onChange={(event) => setStrategy({ ...strategy, mode: event.target.value as 'paper' | 'live' })}
        >
          <option value="paper">Paper</option>
          <option value="live">Live</option>
        </Select>

        <Label style={{ marginTop: 12 }}>Cadence (minutes)</Label>
        <Input
          type="number"
          min="1"
          value={strategy.cadenceMinutes}
          onChange={(event) => setStrategy({ ...strategy, cadenceMinutes: Number(event.target.value) })}
        />

        <Label style={{ marginTop: 12 }}>Min SOL balance</Label>
        <Input
          type="number"
          min="0"
          step="0.01"
          value={strategy.minSolBalance}
          onChange={(event) => setStrategy({ ...strategy, minSolBalance: Number(event.target.value) })}
        />

        <Label style={{ marginTop: 12 }}>Max trade size (SOL)</Label>
        <Input
          type="number"
          min="0"
          step="0.01"
          value={strategy.maxTradeSol}
          onChange={(event) => setStrategy({ ...strategy, maxTradeSol: Number(event.target.value) })}
        />

        <Label style={{ marginTop: 12 }}>Max daily trades</Label>
        <Input
          type="number"
          min="1"
          value={strategy.maxDailyTrades}
          onChange={(event) => setStrategy({ ...strategy, maxDailyTrades: Number(event.target.value) })}
        />

        <Label style={{ marginTop: 12 }}>Allowed mints (comma-separated)</Label>
        <Input
          type="text"
          placeholder="So11111111111111111111111111111111111111112, ..."
          value={strategy.allowedMints.join(', ')}
          onChange={(event) =>
            setStrategy({
              ...strategy,
              allowedMints: event.target.value
                .split(',')
                .map((value) => value.trim())
                .filter(Boolean)
            })
          }
        />

        <div style={{ marginTop: 12 }}>
          <CheckboxRow>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(event) => setEnabled(event.target.checked)}
            />
            Enable strategy
          </CheckboxRow>
        </div>

        <ActionButton type="button" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Strategy'}
        </ActionButton>
        {status && <HelperText>{status}</HelperText>}
        <HelperText>Execution is managed server-side when enabled.</HelperText>
      </Card>
    </PageContainer>
  );
};

export default SentryAgentStrategyPWA;
